/**
 * Pure vertical time-axis geometry for a single day's timeline — how far
 * down an event starts and how tall it is, as fractions (0-1) of a day
 * window. No React, no pixels: the caller multiplies these ratios by
 * whatever container height it's using (or hands them straight to CSS as
 * percentages). Built so Unit 5's week grid can call `getEventGeometry`
 * once per day-column without any change to this module — the column
 * layout and overlap-splitting Unit 5 needs are additive, on top of this.
 *
 * All-day events are NOT this module's concern: a Day/Week view filters
 * `event.allDay` out before calling `getEventGeometry` and renders those in
 * a separate all-day strip instead of on the hour grid.
 */
import { isToday, toLocalDateTime } from './calendarDate';

export interface TimeWindow {
  startMinutes: number;
  endMinutes: number;
}

export interface EventGeometry {
  /** Fraction (0-1) of the window's height where the event starts. */
  topRatio: number;
  /** Fraction (0-1) of the window's height the event occupies. */
  heightRatio: number;
  /** The event's real start is earlier than the window — clipped at the top.
   * A caller can use this to draw a "continues from earlier" affordance. */
  clippedStart: boolean;
  /** The event's real end is later than the window — clipped at the bottom.
   * A caller can use this to draw a "continues later" affordance. */
  clippedEnd: boolean;
}

const MINUTES_PER_DAY = 24 * 60;

/**
 * Minimum visible span. A 15-minute (or zero/negative, bad-data) event is
 * drawn at least this tall so its label stays legible, anchored at its
 * (possibly clipped) start. The floor is applied to the SPAN — not clamped
 * on the rendered block afterwards — so calendarOverlap.ts lane-splits two
 * events whose floored spans collide exactly as they are drawn; a pixel
 * min-height on the block alone would push it into the next slot unnoticed.
 */
export const MIN_VISIBLE_MINUTES = 30;

export const DEFAULT_DAY_WINDOW: TimeWindow = { startMinutes: 0, endMinutes: MINUTES_PER_DAY };

/**
 * Minutes since local midnight of `dayDate`, in `timeZone`. Can be negative
 * (the instant falls on the previous local day) or greater than 1440 (the
 * next local day) — that's how a prior-day or past-midnight event expresses
 * "before/after this day" to `getEventGeometry` below.
 */
export function localMinutesSinceMidnight(
  isoDateTime: string,
  dayDate: { year: number; month: number; day: number },
  timeZone: string,
): number {
  const event = toLocalDateTime(isoDateTime, timeZone);
  const dayStartMs = Date.UTC(dayDate.year, dayDate.month - 1, dayDate.day);
  const eventMs = Date.UTC(event.year, event.month - 1, event.day, event.hour, event.minute);
  return (eventMs - dayStartMs) / 60_000;
}

/**
 * Returns `null` when the event doesn't overlap `window` at all (entirely on
 * an earlier or later day) — the caller simply doesn't render anything for
 * it on this day.
 */
export function getEventGeometry(
  startsAt: string,
  endsAt: string,
  dayDate: { year: number; month: number; day: number },
  timeZone: string,
  window: TimeWindow = DEFAULT_DAY_WINDOW,
): EventGeometry | null {
  const rawStart = localMinutesSinceMidnight(startsAt, dayDate, timeZone);
  const rawEnd = localMinutesSinceMidnight(endsAt, dayDate, timeZone);
  const effectiveRawEnd = Math.max(rawEnd, rawStart + MIN_VISIBLE_MINUTES);

  const clippedStart = rawStart < window.startMinutes;
  const clippedEnd = effectiveRawEnd > window.endMinutes;

  const start = Math.max(rawStart, window.startMinutes);
  const end = Math.min(effectiveRawEnd, window.endMinutes);

  if (end <= window.startMinutes || start >= window.endMinutes) {
    return null;
  }

  const windowSpan = window.endMinutes - window.startMinutes;
  return {
    topRatio: (start - window.startMinutes) / windowSpan,
    heightRatio: (end - start) / windowSpan,
    clippedStart,
    clippedEnd,
  };
}

/**
 * Where the "now" line falls within `window`, or `null` when `dayDate` isn't
 * today (in `timeZone`) or "now" falls outside `window`.
 */
export function getNowLineRatio(
  dayDate: { year: number; month: number; day: number },
  timeZone: string,
  now: Date,
  window: TimeWindow = DEFAULT_DAY_WINDOW,
): number | null {
  if (!isToday(dayDate, timeZone, now)) return null;

  const nowMinutes = localMinutesSinceMidnight(now.toISOString(), dayDate, timeZone);
  if (nowMinutes < window.startMinutes || nowMinutes > window.endMinutes) return null;

  return (nowMinutes - window.startMinutes) / (window.endMinutes - window.startMinutes);
}
